import React, { useContext, useMemo } from 'react';
import { Link, useLocation } from 'react-router';
import { Box } from '@mui/material';
import { ITrainingSet } from '../../types/my-types';
import TrainingSetName from './details/TrainingSetName';
import TrainingSetDelete from './actions/TrainingSetDelete';
import TrainingSetResetValidationRules from './actions/TrainingSetResetValidationRules';
import TrainingSetCertify from './actions/TrainingSetCertify';
import TrainingRevisionCreate from './revisions/TrainingRevisionCreate';
import TrainingSetAddDocuments from './actions/TrainingSetAddDocuments';
import TrainingSetAddDocumentsById from './actions/TrainingSetAddDocumentsById';
import DownloadDataReport from './actions/DownloadDataReport';
import TrainingSetVendor from './TrainingSetVendor';
import TrainingSetLanguage from './TrainingSetLanguage';
import TrainingSetStats from './TrainingSetStats';
import { UploadContextProvider } from '../upload/UploadContext';
import { AppContext } from '../../App';
import {
  DivFlexHeader,
  DivFlexHeaderIcons,
  DivVerticalDividerLarge,
  DivVerticalDividerSmall,
  TypographyPropertiesTitleStyled,
  TypographyPropertiesDataStyled,
} from '../common/StyledComponents';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { getIdFromUrl } from '../../utils/getIdFromUrl';
import usePaginatedTrainingRevisions from '../hooks/usePaginatedTrainingRevisions';

interface IProps {
  trainingSet: ITrainingSet | undefined;
  dpid: string;
  tsid: string;
}

const TrainingSetHeader = ({ trainingSet, dpid, tsid }: IProps) => {
  const location = useLocation();
  const { isStaff, setToast } = useContext(AppContext);

  const { data: trainingRevisions } = usePaginatedTrainingRevisions({
    trainingSetId: tsid,
    limit: 100,
    defaultPage: 0,
  });

  const isTrainingInProgress = useMemo(() => {
    return trainingRevisions?.results?.some(
      (tr) => tr.status === 'In Progress' || tr.status === 'Pending'
    ) ?? false;
  }, [trainingRevisions]);

  const selectedTab = React.useMemo(() => {
    const p = location.pathname;
    if (p.includes('/train/') && p.includes('/mark/')) return 'mark';
    if (p.includes('/train/') && p.includes('/fields')) return 'fields';
    if (p.includes('/train/') && p.includes('/details')) return 'details';
    return 'details';
  }, [location.pathname]);

  // Extract the first document ID for the mark URL
  const firstDocumentId = React.useMemo(() => {
    return trainingSet && trainingSet.documents && trainingSet.documents.length > 0
      ? getIdFromUrl(trainingSet.documents[0])
      : '';
  }, [trainingSet]);

  const disabled = trainingSet?.training_requested && !isStaff;
  const noDoc = trainingSet?.documents.length === 0;

  return (
    <>
      <DivFlexHeader>
        <TrainingSetName tsCharLimit={35} tsNameAsLink={false} />

        <div style={{ display: 'flex', alignItems: 'center' }}>
          {trainingSet ? (
            <DivFlexHeaderIcons>
              <div>
                <TypographyPropertiesTitleStyled>ID</TypographyPropertiesTitleStyled>
                <TypographyPropertiesDataStyled>
                  <span
                    onClick={() => {
                      navigator.clipboard.writeText(trainingSet.id || '');
                      setToast({
                        open: true,
                        message: 'Copied!',
                        variant: 'success',
                      });
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {trainingSet.id}
                  </span>
                </TypographyPropertiesDataStyled>
              </div>

              <DivVerticalDividerSmall />

              <div>
                <TypographyPropertiesTitleStyled>DOCUMENTS</TypographyPropertiesTitleStyled>
                <TypographyPropertiesDataStyled>
                  {trainingSet.documents.length ?? trainingSet.document_count}
                </TypographyPropertiesDataStyled>
              </div>

              <DivVerticalDividerSmall />

              <TrainingSetLanguage trainingSet={trainingSet} />

              <DivVerticalDividerSmall />

              {trainingSet.total_meter_count !== undefined && (
                <>
                  <div>
                    <TypographyPropertiesTitleStyled>METERS</TypographyPropertiesTitleStyled>
                    <TypographyPropertiesDataStyled>
                      {trainingSet.total_meter_count}
                    </TypographyPropertiesDataStyled>
                  </div>
                  <DivVerticalDividerSmall />
                </>
              )}

              <TrainingSetVendor trainingSet={trainingSet} />

              <DivVerticalDividerLarge />

              <TrainingSetStats trainingSet={trainingSet} />
              <TrainingRevisionCreate trainingSet={trainingSet} type="icon" isTrainingInProgress={isTrainingInProgress} />
              <UploadContextProvider>
                <TrainingSetAddDocuments trainingSet={trainingSet} />
              </UploadContextProvider>
              <TrainingSetAddDocumentsById trainingSet={trainingSet} />
              <DownloadDataReport trainingSet={trainingSet} disabled={disabled || noDoc} />
              <TrainingSetResetValidationRules trainingSetId={trainingSet.id} />
              <TrainingSetCertify trainingSet={trainingSet} dpid={dpid} />
              {isStaff ? (
                <TrainingSetDelete trainingSetId={trainingSet.id} type="icon" forwardUser={true} />
              ) : null}
            </DivFlexHeaderIcons>
          ) : null}
        </div>
      </DivFlexHeader>
      <Box sx={{ mt: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        <Tabs
          value={selectedTab}
          aria-label="Training set sections"
          role="tablist"
          sx={{
            minHeight: 36,
            '& .MuiTab-root': {
              minHeight: 36,
              px: 2,
              py: 0.75,
              fontSize: '13px',
              fontWeight: 500,
              textTransform: 'none',
              color: '#5f6d7e',
              letterSpacing: '0.2px',
              '&:hover': {
                color: '#1a2332',
              },
              '&.Mui-selected': {
                color: '#1D9AAA',
                fontWeight: 600,
              },
              '&.Mui-disabled': {
                opacity: 0.4,
              },
            },
            '& .MuiTabs-indicator': {
              height: 2,
              borderRadius: '2px 2px 0 0',
              backgroundColor: '#1D9AAA',
            },
          }}
        >
          <Tab
            label="Details"
            value="details"
            component={Link}
            to={`/data-pools/${dpid}/train/${tsid}/details`}
            aria-controls="tab-ts-details"
            data-cy="tab-ts-details"
          />
          <Tab
            label="Fields"
            value="fields"
            component={Link}
            to={`/data-pools/${dpid}/train/${tsid}/fields`}
            aria-controls="tab-ts-fields"
            data-cy="tab-ts-fields"
          />
          <Tab
            label="Mark"
            value="mark"
            component={Link}
            to={`/data-pools/${dpid}/train/${tsid}/mark/${firstDocumentId}`}
            aria-controls="tab-panel-mark"
            data-cy="tab-ts-mark"
            disabled={
              !trainingSet?.documents ||
              trainingSet.documents.length === 0 ||
              !firstDocumentId
            }
          />
        </Tabs>
      </Box>
    </>
  );
};

export default TrainingSetHeader;
